import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ClipboardList, 
  Clock, 
  CalendarDays, 
  Grid, 
  X, 
  Users, 
  FileText, 
  FileCheck, 
  Package, 
  Wrench, 
  Truck, 
  Building2, 
  Map, 
  Settings, 
  Shield, 
  LogOut, 
  Moon, 
  Sun, 
  Monitor,
  ListChecks
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../hooks/useTheme';

export const MobileNavBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, currentTenant, logout } = useAuth();
  const { theme, cycleTheme } = useTheme();
  const [showMenuSheet, setShowMenuSheet] = useState(false);

  const themeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const themeLabel = theme === 'dark' ? 'Dunkel' : theme === 'light' ? 'Hell' : 'System';
  const ThemeIcon = themeIcon;

  const hasAccess = (moduleId: string | null) => {
    if (!currentUser) return false;
    if (moduleId === null) return true;
    if (currentUser.role === 'Admin' && moduleId === 'verwaltung') return true;
    return (currentUser.activeModules || []).includes(moduleId);
  };

  // All additional modules for the "Mehr / Menü" Sheet
  const allModules = [
    { path: '/disposition', label: 'Disposition', icon: ListChecks, moduleId: 'auftraege', color: '#0078d4' },
    { path: '/crm', label: 'Kunden (CRM)', icon: Users, moduleId: 'crm', color: '#107c41' },
    { path: '/angebote', label: 'Angebote', icon: FileText, moduleId: 'angebote', color: '#b45309' },
    { path: '/rechnungen', label: 'Rechnungen', icon: FileCheck, moduleId: 'rechnungen', color: '#5c2d91' },
    { path: '/lager', label: 'Lager & Material', icon: Package, moduleId: 'lager', color: '#0078d4' },
    { path: '/maschinen', label: 'Maschinen', icon: Wrench, moduleId: 'maschinen', color: '#d13438' },
    { path: '/fuhrpark', label: 'Fuhrpark', icon: Truck, moduleId: 'fuhrpark', color: '#0284c7' },
    { path: '/karte', label: 'Einsatz-Karte', icon: Map, moduleId: 'karte', color: '#107c41' },
    { path: '/mitarbeiter', label: 'Mitarbeiter', icon: Users, moduleId: 'mitarbeiter', color: '#b45309' },
    { path: '/lieferanten', label: 'Lieferanten', icon: Building2, moduleId: 'lieferanten', color: '#5c2d91' },
    { path: '/admin', label: 'Verwaltung', icon: Settings, moduleId: 'verwaltung', color: '#605e5c' },
    { path: '/protokoll', label: 'GoBD-Protokoll', icon: Shield, moduleId: 'protokoll', color: '#605e5c' }
  ].filter(m => hasAccess(m.moduleId));

  return (
    <>
      {/* iOS-Style Bottom Tab Bar */}
      <nav className="mobile-tab-bar" aria-label="Mobile Navigation">
        {/* 1. Home / Dashboard */}
        <NavLink 
          to="/" 
          className={({ isActive }) => `mobile-tab-item ${isActive ? 'active' : ''}`}
        >
          <LayoutDashboard size={21} />
          <span>Übersicht</span>
        </NavLink>

        {/* 2. Aufträge */}
        {hasAccess('auftraege') && (
          <NavLink 
            to="/auftraege" 
            className={({ isActive }) => `mobile-tab-item ${isActive ? 'active' : ''}`}
          >
            <ClipboardList size={21} />
            <span>Aufträge</span>
          </NavLink>
        )}

        {/* 3. Zeiterfassung (Elevated Center Quick Action) */}
        {hasAccess('zeiterfassung') && (
          <NavLink 
            to="/zeiterfassung" 
            className={({ isActive }) => `mobile-tab-item highlight-action ${isActive ? 'active' : ''}`}
          >
            <div className="tab-icon-bubble">
              <Clock size={22} color="#ffffff" />
            </div>
            <span>Stempeluhr</span>
          </NavLink>
        )}

        {/* 4. Einsatzplan */}
        {hasAccess('einsatzplan') && (
          <NavLink 
            to="/einsatzplan" 
            className={({ isActive }) => `mobile-tab-item ${isActive ? 'active' : ''}`}
          >
            <CalendarDays size={21} />
            <span>Plan</span>
          </NavLink>
        )}

        {/* 5. Mehr / Menü Button */}
        <button 
          onClick={() => setShowMenuSheet(true)}
          className={`mobile-tab-item ${showMenuSheet ? 'active' : ''}`}
          aria-label="Alle Module öffnen"
        >
          <Grid size={21} />
          <span>Menü</span>
        </button>
      </nav>

      {/* Full App-Grid Bottom Sheet */}
      {showMenuSheet && (
        <div className="mobile-menu-sheet" onClick={() => setShowMenuSheet(false)}>
          <div className="mobile-menu-content" onClick={e => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />

            {/* Header with User and Close */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--ms-text-primary)' }}>
                  Alle Module
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: 'var(--ms-text-secondary)' }}>
                  {currentUser?.name} · {currentTenant?.name || 'Betriebszentrale'}
                </p>
              </div>
              <button 
                onClick={() => setShowMenuSheet(false)}
                style={{ 
                  background: 'var(--ms-bg-surface-secondary)', 
                  border: '1px solid var(--ms-border)', 
                  borderRadius: '50%', 
                  width: '32px', 
                  height: '32px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  color: 'var(--ms-text-primary)',
                  cursor: 'pointer' 
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Touch-Optimized 3-Column App Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '10px',
              padding: '6px 0'
            }}>
              {allModules.map(item => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      setShowMenuSheet(false);
                      navigate(item.path);
                    }}
                    style={{
                      background: isActive ? 'var(--ms-blue-light)' : 'var(--ms-bg-surface-secondary)',
                      border: '1px solid',
                      borderColor: isActive ? 'var(--ms-blue-primary)' : 'var(--ms-border)',
                      borderRadius: '12px',
                      padding: '14px 8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      transition: 'all var(--transition-fast)',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background: 'var(--ms-bg-surface)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: item.color || 'var(--ms-blue-primary)',
                      boxShadow: 'var(--sys-elevation-1)'
                    }}>
                      <item.icon size={20} />
                    </div>
                    <span style={{ 
                      fontSize: '0.78rem', 
                      fontWeight: isActive ? 700 : 600, 
                      color: isActive ? 'var(--ms-blue-primary)' : 'var(--ms-text-primary)',
                      lineHeight: 1.2
                    }}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Bottom Actions (Theme & Logout) */}
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              paddingTop: '10px', 
              borderTop: '1px solid var(--ms-border-subtle)',
              marginTop: '4px'
            }}>
              <button
                onClick={cycleTheme}
                className="btn secondary"
                style={{ flex: 1, height: '42px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '0.85rem' }}
              >
                <ThemeIcon size={16} />
                <span>Theme: {themeLabel}</span>
              </button>

              <button
                onClick={() => {
                  setShowMenuSheet(false);
                  logout();
                }}
                className="btn danger"
                style={{ flex: 1, height: '42px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '0.85rem' }}
              >
                <LogOut size={16} />
                <span>Abmelden</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
